import React from 'react';
import { Grid, Box, Typography, CircularProgress, Skeleton } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import TimerIcon from '@mui/icons-material/Timer';

const CARD_ACCENTS = {
  available:       { icon: '#1976d2', bg: 'rgba(25,118,210,0.10)',  border: '#e3eeff' },
  assigned:        { icon: '#7c3aed', bg: 'rgba(124,58,237,0.10)',  border: '#ede9fe' },
  completed:       { icon: '#16a34a', bg: 'rgba(22,163,74,0.10)',   border: '#dcfce7' },
  todayCommission: { icon: '#d97706', bg: 'rgba(217,119,6,0.10)',   border: '#fef3c7' },
  acceptance:      { icon: '#0284c7', bg: 'rgba(2,132,199,0.10)',   border: '#e0f2fe' },
  lost:            { icon: '#dc2626', bg: 'rgba(220,38,38,0.10)',   border: '#fee2e2' },
  avgAcceptTime:   { icon: '#0891b2', bg: 'rgba(8,145,178,0.10)',   border: '#cffafe' },
};

export default function AgentKPICards({ data, cards: customCards, loading, onCardClick, highlightId, onHighlightClick }) {
  const values = data || {};

  const defaultCards = [
    {
      id: 'available',
      title: 'Available Orders',
      icon: <InboxIcon />,
      value: values.available ?? 0,
    },
    {
      id: 'assigned',
      title: 'Active Orders',
      icon: <AssignmentTurnedInIcon />,
      value: values.assigned ?? 0,
    },
    {
      id: 'completed',
      title: 'Delivered Orders',
      icon: <CheckCircleIcon />,
      value: values.completed ?? 0,
    },
    {
      id: 'todayCommission',
      title: "Commission",
      icon: <MonetizationOnIcon />,
      value: values.todayCommission ?? '₹0',
    },
    {
      id: 'acceptance',
      title: 'Acceptance Rate',
      icon: <SpeedIcon />,
      value: values.acceptance ?? 0,
      progress: true,
    },
    {
      id: 'lost',
      title: 'Orders Lost',
      icon: <TimerOffIcon />,
      value: values.lost ?? 0,
    },
    {
      id: 'avgAcceptTime',
      title: 'Avg Accept Time',
      icon: <TimerIcon />,
      value: values.avgAcceptTime ?? '—',
    },
  ];

  const cards = customCards || defaultCards;

  return (
  <Box
    sx={{
      display: 'grid',
      gap: 1.5,
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(4, 1fr)',
        lg: 'repeat(4, 1fr)',
        xl: 'repeat(7, 1fr)',
      },
      alignItems: 'stretch',
    }}
  >
    {cards.map((c) => {
      const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.available;
      const isHighlighted = c.id === highlightId;

      return (
        <Box
          key={c.id}
          onClick={() => {
            onCardClick?.(c.id);
            if (isHighlighted) onHighlightClick?.();
          }}
          role={onCardClick ? 'button' : undefined}
          tabIndex={onCardClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onCardClick && (e.key === 'Enter' || e.key === ' ')) {
              onCardClick(c.id);
              if (isHighlighted) onHighlightClick?.();
            }
          }}
          sx={{
            p: 1.25,
            borderRadius: '12px',
            bgcolor: '#fff',
            border: `1.5px solid ${
              isHighlighted ? '#f59e0b' : accent.border
            }`,
            boxShadow: isHighlighted
              ? '0 0 0 3px rgba(245,158,11,0.22), 0 4px 20px rgba(15,23,42,0.08)'
              : '0 2px 10px rgba(15,23,42,0.06)',
            cursor: onCardClick ? 'pointer' : 'default',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',

            display: 'flex',
            alignItems: 'center',
            gap: {
              xs: 1,
              sm: 1.25,
            },

            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',

            minHeight: {
               xs: 80,
               sm: 80,
               md: 90,
            },

            '&:hover': onCardClick
              ? {
                  transform: 'translateY(-2px)',
                  boxShadow:
                    '0 8px 24px rgba(15,23,42,0.10)',
                }
              : {},
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: {
                xs: 34,
                sm: 38,
                lg: 36,
              },
              height: {
                xs: 34,
                sm: 38,
                lg: 36,
              },
              borderRadius: '10px',
              bgcolor: accent.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {React.cloneElement(c.icon, {
              sx: {
                fontSize: {
                  xs: 18,
                  sm: 20,
                  lg: 18,
                },
                color: accent.icon,
              },
            })}
          </Box>

          {/* Text */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: {
                  xs: '0.65rem',
                  sm: '0.7rem',
                  lg: '0.68rem',
                },
                fontWeight: 500,
                color: '#64748b',
                lineHeight: 1.2,
                mb: 0.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {c.title}
            </Typography>

            {loading ? (
              <Skeleton
                width="70%"
                height={24}
                sx={{ borderRadius: 1 }}
              />
            ) : c.progress ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: '1.1rem',
                      sm: '1.2rem',
                      lg: '1rem',
                    },
                    fontWeight: 700,
                    lineHeight: 1,
                    color: '#0f172a',
                  }}
                >
                  {typeof c.value === 'number'
                    ? `${c.value}%`
                    : c.value}
                </Typography>

                <CircularProgress
                  variant="determinate"
                  value={Number(c.value)}
                  size={22}
                  thickness={5}
                  sx={{
                    color: accent.icon,
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: {
                    xs: '1.1rem',
                    sm: '1.2rem',
                    lg: '1rem',
                  },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: '#0f172a',
                }}
              >
                {c.value}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);
}